/**
 * aggregate (#2457, reworked by HOF-6) — pure aggregation of individual price
 * observations into ONE representative price per BUCKET, split by side and by
 * channel, with every underlying observation kept for the drill-down panel.
 *
 * ── What HOF-6 changed, and why each half matters ───────────────────────────
 *
 * **The statistic is now the VOLUME-WEIGHTED AVERAGE**, Σ money ÷ Σ quantity,
 * replacing the unweighted per-day median (p50) with its p10–p90 whisker. The
 * median was chosen for ROBUSTNESS — one mis-parsed observation (a €/Stück
 * booked as €/kg) could not move it — and it bought that robustness by giving a
 * 500 g punnet exactly as much say as a 12 t delivery. For a price monitor that
 * is the wrong trade: the farmer's question is "what did I realise", and the
 * answer to that is money over kilos. Weighting recovers the robustness anyway,
 * from the other side — a mis-booked 0,1 kg line at 52 €/kg is 0,05 % of a
 * 200 kg day and moves the price by nothing — and it is the same number the
 * SERVER already computes per cell (`volume_weighted_mean`), so the two grains
 * finally agree about one population.
 *
 * The p10/p90 pair went with it (they existed only to draw the whisker HOF-6
 * removed). `obsMin`/`obsMax` — the TRUE extent — stay, because the panel still
 * quotes a Spanne over the very rows it lists.
 *
 * **A bucket is now a grain, not always a day.** `aggregateObservations` takes a
 * {@link Grain} and folds 1T/1W/1M buckets directly, so a week's price is Σ€ over
 * the whole week ÷ Σkg over the whole week. Deliberately NOT the mean of the
 * days' means, which would re-introduce exactly the equal-say defect the
 * weighting removes, one level up.
 *
 * **Sides are a model dimension, not a filter.** The Kauf/Verkauf switcher is
 * gone and both lines are drawn at once, so the split has to live here — an
 * aggregate spanning both sides would average a purchase price into a sale
 * price. Groups are keyed `(side, channel|'all')` by {@link groupKey}.
 *
 * Nothing is hidden: every observation is carried in `sources` for the
 * drill-down, so this summarises for legibility without ever averaging away a
 * source.
 */
import { CHANNELS, PRICE_SIDES, type Channel, type PriceSide } from './catalog';
import { bucketEndEpochDay, bucketStartEpochDay, epochDayToIso, type Grain } from './grain';
import { toEpochDay } from './priceAt';
import type { OutOfBandMark, PointProvenance, PriceSeries } from './types';

/** One underlying observation kept for the bucket-detail drill-down. */
export interface AggSource {
  counterparty: string;
  channel: Channel | null;
  value: number;
  provenance?: PointProvenance;
  /**
   * How many source invoice lines this observation stands for (#2646).
   *
   * 1 on the line-grain read path, where an observation IS a line. On the
   * cell-backed path one observation is a whole ``(day × series)`` aggregate,
   * so this carries the cell's row count — which is what keeps every "N
   * Beobachtungen" / "N Positionen" readout counting lines rather than
   * silently switching to counting cells.
   */
  lineCount: number;
  /**
   * The lowest / highest price behind this observation (#2671) — a LINE price
   * where the cell stated one, otherwise a sub-cell's own price standing in.
   * Absent where the observation cannot state a range (always at line grain).
   * See ``types.ObservedPoint.obsMin`` for when that is reachable.
   *
   * `value` is a single price — at cell grain a volume-weighted mean over a
   * whole day of one counterparty — so these are the only place the day's real
   * spread survives. Read them as `obsMin ?? value`: that fallback is what
   * keeps the line-grain path, where an observation IS one line, byte-identical.
   */
  obsMin?: number;
  obsMax?: number;
  /** Soft hint for the detail list: far from the bucket's price (likely bad data). */
  isOutlier: boolean;
  /**
   * The server's plausibility verdict (HOF-19) — this observation's €/kg is
   * outside its crop's declared band.
   *
   * **Not the same thing as {@link isOutlier}, and the pair is worth keeping
   * apart.** `isOutlier` is a soft, RELATIVE hint: far from THIS bucket's own
   * price, computed here, meaningful only inside the bucket, and it changes when
   * the bucket's other members change. This is an ABSOLUTE verdict against a
   * declared, reviewed window (`eur_per_kg_bands`), computed on the server, and
   * it is the same answer whatever else the farm sold that day. Only this one
   * moves the average; `isOutlier` never has and still does not.
   */
  outOfBand?: OutOfBandMark;
  /** The ISO day this observation was booked on — a bucket may span several. */
  date: string;
  /**
   * The series this observation came from (HOF-19).
   *
   * Carried so a panel row can ask for its own cell's source LINES —
   * ``(item, series_id, date)`` is exactly what
   * ``GET /twin/preisspiegel/lines`` requires, and a row that could not name its
   * series would have to be answered by widening the request, which is the bulk
   * walk AC 10 forbids.
   *
   * Optional, because ``AggSource`` is also built from the line-grain
   * ``ownPriceAdapter`` path (retained as the #2649 equivalence proof), and an
   * absent series simply means "no drill-down offered here" rather than a
   * request nobody can fulfil.
   */
  seriesId?: string;
}

/**
 * One bucket-detail ROW (#2539): the observations that a reader cannot tell apart —
 * same counterparty, same channel, same exact price — collapsed into one entry.
 *
 * One ``price_observation`` fact is emitted per ``event_line``, so a buyer who ordered the
 * same article in three positions on one Lieferschein produces three byte-identical rows.
 * The per-line grain is deliberate in the twin (each observation carries its own
 * ``event_id``/``line_id``/``amount``, so the panel reconciles back to the source lines) —
 * this collapse is presentation-only and lives beside, never instead of, ``sources``.
 */
export interface AggSourceGroup extends AggSource {
  /** How many source lines (``event_line``s) this row stands for — Σ of the members'. */
  lineCount: number;
  /** Σ of the members' line amounts in €, or null when ANY member lacks one. */
  amountSum: number | null;
  /**
   * #2649 — Σ of the members' TRADED WEIGHT in catalog units (kg), or null when
   * ANY member lacks a usable one. See {@link catalogUnitKg} for the identity
   * and {@link KG_PROVENANCE_FIELDS} for what it reads.
   *
   * HOF-340 — in the basis of the tile's CATALOG UNIT, which is not always the
   * quantity a reader is owed: on a `ct/l` tile it is hectolitres. Every DISPLAY
   * of this number goes through `publishedUnit.formatTradedQuantity`, which
   * applies the numerator factor; nothing in this module does, because both
   * consumers here — the weighted fold and the sort — are scale-invariant.
   */
  kgSum: number | null;
  /**
   * #3593 — how much of this row entered the bucket's WEIGHTED fold: `all`,
   * `some` or `none` of its member observations.
   *
   * Carried out of {@link collapseSources} rather than re-derived from
   * {@link kgSum}, because the sum is NOT `bucketPrice`'s predicate once a row
   * collapses several members, and the two disagree in both directions:
   *
   *  - a **positive + missing** group nulls `kgSum` under the whole-row-or-none
   *    contract, so the sum reads as excluded while the positive member really
   *    did contribute to Σ kg;
   *  - a **positive + stated-0** group keeps a positive `kgSum`, so the sum
   *    reads as included while the 0 kg member really was dropped.
   *
   * Both are reachable — the collapse key is (counterparty, channel, date,
   * price, outlier), so two lines of one delivery differing only in quantity
   * land in one row. The panel labels a row by what it actually contributed, so
   * it needs the per-member answer, not the aggregate.
   */
  weightShare: 'all' | 'some' | 'none';
}

/**
 * The ``PointProvenance`` field the traded weight is derived from — named
 * explicitly so a change to WHERE prices are read from cannot drop it silently
 * (#2649). The other half of the identity is the point's own ``value``, which
 * every read path carries by definition.
 *
 * HOF-6 made this LOAD-BEARING FOR THE LINE ITSELF, not just for the panel's
 * sort order: the plotted price is now Σ amount ÷ Σ (amount/value). A read path
 * that returns only ``{date, value}`` per point degrades the whole chart from a
 * volume-weighted average to a plain mean, and does it QUIETLY — the line still
 * draws, the numbers are still plausible, and only ``BucketAgg.weighted`` says
 * otherwise. ``__tests__/tradedWeightProvenance.test.ts`` asserts its PRESENCE
 * on a normal row, end to end, for exactly that reason.
 */
export const KG_PROVENANCE_FIELDS: readonly (keyof PointProvenance)[] = ['amount'];

/**
 * One observation's traded weight in CATALOG units (kg), or null where the
 * source line does not support one: ``amount / value`` — the money on the line
 * divided by the €/kg it resolved to.
 *
 * WHICH IDENTITY, AND WHY — the tempting alternative is WRONG. ``provenance``
 * also carries ``quantity`` and ``unit_factor``, and the server's own volume
 * weighting divides one by the other (``weight_key="provenance.quantity"`` +
 * ``weight_divisor_key="provenance.unit_factor"``, ``backend/src/api/v1/
 * endpoints/twin/preisspiegel.py``), which reads as the exact, authoritative
 * identity. It is not one, because ``unit_factor`` converts a PRICE BASIS, not a
 * quantity. ``config/catalog/preisspiegel-crosswalk@v1.yaml`` maps three units
 * per item, and only two of the three make the division meaningful:
 *
 *   - ``kg: 1.0`` — quantity already in kg. ``quantity / 1.0`` ✓
 *   - ``gebinde: net_kg`` — priced per package, factor ``1 / net_kg``, so
 *     ``quantity / factor`` = packages × net_kg ✓
 *   - ``kolli|steige|schale: 1.0`` — the line's unit price is ALREADY €/kg while
 *     its quantity counts PACKAGES (the crosswalk's own comment: "a FRUTANIA
 *     Erdbeeren line quantified in Kolli carries a unit_price already
 *     denominated in €/kg … so Kolli → factor 1.0"). ``quantity / 1.0`` is a
 *     PACKAGE COUNT ✗ — 72 Kolli would read "72 kg" for 360 traded kilos.
 *
 * That last shape is the dominant grosshandel one, so the "exact" identity would
 * be wrong by the whole pack size on the rows that move the most weight. The FE
 * cannot tell the cases apart either: distinguishing them means re-deriving the
 * crosswalk's price-basis knowledge, which is backend-owned by explicit contract
 * (``ownPriceAdapter``'s ownership note) — and mixing the two identities inside
 * one sum would misstate a row exactly as a partial sum would.
 *
 * ``amount / value`` sidesteps all of it: both sides are money-and-price on the
 * SAME basis by construction of the fact, so it yields real kilos under all
 * three mappings. Its cost is honest and much smaller — it is derived from the
 * line total, so a storno-netted or rebated ``amount`` understates the weight
 * slightly, and ``value`` is rounded to 4 dp. Both are far below the display
 * precision (one decimal), whereas a pack-size error is a factor of 5.
 *
 * HOF-340 — and it is a quantity only where the price's NUMERATOR is euros.
 * ``amount`` is money in €; ``value`` is a price in the unit the fact declares,
 * and ``ct/l`` is the one member of ``PRICE_UNITS`` that is not €-denominated,
 * so there the quotient is hectolitres. That is deliberately NOT corrected here:
 * this identity feeds ``bucketPrice``'s weighted fold and the drill-down's sort
 * key, and both are scale-invariant (a uniform factor cancels exactly in
 * ``Σ(value × kg) / Σ kg``, and is order-preserving in a sort), so a factor
 * applied here would reach the PRICE path for no gain. The correction belongs at
 * the display, where the quotient leaves the weighting and nothing cancels —
 * ``publishedUnit.formatTradedQuantity``. ``config/metrics/price_observation.yaml``
 * §146-149 states the same identity and the same caveat from the backend side.
 *
 * Null (rather than 0 or NaN) whenever the weight is UNKNOWN: no ``amount`` on
 * the line (a real, reachable state — the materialiser emits ``amount: None``
 * where the line carried none), a non-finite one, or a non-positive ``value``
 * (#2561 makes that unreachable on an emitted fact, but a divide-by-zero must
 * never reach the fold as ``Infinity``). An ``amount`` of 0 is a KNOWN 0 kg.
 */
export function catalogUnitKg(provenance: PointProvenance | undefined, value: number): number | null {
  const amount = provenance?.amount;
  if (amount == null || !Number.isFinite(amount)) return null;
  if (!Number.isFinite(value) || value <= 0) return null;
  const kg = amount / value;
  // A negative weight would misstate the row's total exactly as a partial sum would.
  return Number.isFinite(kg) && kg >= 0 ? kg : null;
}

/**
 * One bucket's aggregated price for one group (a side, all channels or one).
 *
 * `date` is the bucket's START and `endDate` the last day it covers, so every
 * consumer that captions a point can name the PERIOD rather than its first day
 * — a weekly average captioned "17.08.2026" reads as one Monday's trading.
 */
export interface BucketAgg {
  /** Bucket START, ISO YYYY-MM-DD. The `?d=` key and the chart's x anchor. */
  date: string;
  /** Last day the bucket covers, inclusive. Equal to `date` at day grain. */
  endDate: string;
  /**
   * The bucket's price: Σ money ÷ Σ traded quantity across every observation in
   * it (HOF-6). See {@link weighted} for when that Σ could not be taken.
   */
  price: number;
  /**
   * Whether {@link price} is genuinely volume-weighted — i.e. at least one
   * observation carried a usable traded quantity.
   *
   * `false` means the fallback fired and the price is a line-count-weighted mean
   * of the observed values. Surfaced rather than hidden because the chart's
   * footer states the method: claiming a weighting that did not happen is the
   * quiet kind of wrong, and the state is genuinely reachable (a read path that
   * carries no `provenance.amount` degrades every bucket at once).
   */
  weighted: boolean;
  /**
   * Whether {@link price} is volume-weighted but NOT over every observation in
   * the bucket — i.e. at least one observation stated a quantity and at least
   * one did not, so the volume-less ones were excluded from the average.
   *
   * Always `false` when {@link weighted} is `false`: the fallback branch folds
   * every observation, so nothing was excluded. The pair is what lets the footer
   * distinguish "weighted average of ALL transactions" from "weighted average of
   * the transactions that stated a quantity" — the second is the true sentence
   * whenever this is set, and the first was being printed for both (v34.6.0
   * release gate).
   */
  partiallyWeighted: boolean;
  /**
   * The bucket's TRUE observed extent — what the panel's "Spanne (Min–Max)"
   * renders, folded by {@link observationExtent}.
   *
   * Always present: a bucket has at least one observation, and an observation
   * that states no range of its own contributes its `value`. So at line grain
   * this is simply min/max over the prices, and at cell grain it reaches into
   * the ranges the cells stated.
   */
  obsMin: number;
  obsMax: number;
  /** Source LINES in the bucket (Σ of the observations' `lineCount`). */
  count: number;
  /** Every observation in the bucket, sorted by value desc. */
  sources: AggSource[];
  /**
   * The panel's rows: `sources` with indistinguishable lines collapsed, ordered
   * by TRADED WEIGHT desc (#2649) with weightless rows at the tail in value-desc
   * order. `sources` keeps its own value-desc order untouched.
   */
  uniqueSources: AggSourceGroup[];
  /** Distinct counterparties in the bucket — the honest "N Quellen". */
  counterpartyCount: number;
  /**
   * How many observations in this bucket were left OUT of {@link price} because
   * the server marked them out of band (HOF-19). ``0`` on a clean bucket.
   *
   * Counts observations, not lines, matching the grain of {@link sources} —
   * ``count`` is the line total and would answer a different question.
   *
   * Read by the chart footnote, which may only state the exclusion when the
   * DRAWN WINDOW actually contains one (AC 17). Copy describing a rule that did
   * not fire is the same defect as copy describing a glyph the reader cannot
   * see — the #2777 estimate caption and the bridge clause both learnt it.
   */
  excludedCount: number;
  /**
   * EVERY observation in this bucket was marked, so {@link price} is folded over
   * marked data — there was no trustworthy subset to average.
   *
   * The bucket still has a price and is still plotted, because a bucket that
   * vanished would be the suppression this ticket exists to forbid, reached by
   * omission. This flag is what lets the chart mark the point instead, so the
   * reader sees a value AND sees that nothing here is vouched for.
   *
   * Reachable and not rare: HOF-17's artefact sat in a channel series with
   * exactly one member.
   */
  allExcluded: boolean;
}

/** A group's key: one side's combined line, or one of its channels. */
export type GroupKey = string;

/**
 * The channel slot of an observation that states NO channel.
 *
 * `channel` is an optional axis (``catalog.generated.ts``), and
 * ``ownPriceAdapter`` drops one it cannot validate — so "no channel" is the
 * ordinary case, not an edge. It still has to be a SLOT rather than an absence,
 * because the drill-down panel's columns are the per-channel groups: an
 * observation filed under no channel at all is filed out of the panel, and the
 * reader's click lands on a point that draws its selection ring and then shows
 * nothing beneath it. Same defect one level up in `Nach Kanal`, where the plot
 * simply omits it.
 *
 * Deliberately NOT added to the catalog's `CHANNELS`: that list is the
 * vocabulary a fact may be WRITTEN with, and this is what we call the remainder
 * when a fact declined to. Keeping them apart is what stops "ohne" from ever
 * being offered as a channel to write.
 */
export const NO_CHANNEL = 'ohne';

/** A channel, or the remainder that states none. See {@link NO_CHANNEL}. */
export type ChannelSlot = Channel | typeof NO_CHANNEL;

/**
 * The key a `(side, channel)` group is filed under.
 *
 * ONE function rather than template literals at the call sites, because the
 * model, the chart's line specs and the panel's columns all have to agree about
 * it — and a mismatch there is a line that silently draws nothing rather than an
 * error.
 *
 * `__` and not a punctuation separator: this key becomes part of a recharts
 * `dataKey`, which recharts resolves with a lodash-style path `get`. A `.` would
 * be read as a nested lookup and a `|`/`[` would be one bad release away from
 * the same. Both halves are lowercase ASCII enum values, so `__` cannot collide.
 */
export function groupKey(side: PriceSide, channel: ChannelSlot | 'all'): GroupKey {
  return `${side}__${channel}`;
}

export interface AggregateModel {
  /**
   * Bucket series per group — one entry per side (`channel = 'all'`) and one per
   * `(side, channel)` pair that carries data. Ascending by date.
   */
  byGroup: Record<GroupKey, BucketAgg[]>;
  /** Sides present, in canonical PRICE_SIDES order. */
  sidesPresent: PriceSide[];
  /**
   * Channel slots present per side, in canonical CHANNELS order with the
   * {@link NO_CHANNEL} remainder last.
   *
   * "Present" means BACKED BY OBSERVATIONS, which is why the remainder belongs
   * here rather than being synthesised by the consumer: a side whose facts all
   * carry a channel must not grow an empty "Ohne Kanal" column, and a side that
   * mixes the two must not lose half its population to a panel that only knows
   * about channels.
   */
  channelsBySide: Partial<Record<PriceSide, ChannelSlot[]>>;
  /**
   * Every bucket start observed on ANY side, ascending and de-duplicated.
   *
   * This is what `?d=` and the selected-bucket readout resolve against.
   * Deliberately a list of DATES rather than a combined `BucketAgg[]`: a bucket
   * spanning both sides would have to state one price for a purchase and a sale
   * at once, and there is no such number.
   */
  bucketDates: string[];
  /** The grain the buckets were folded at, so consumers can caption a period. */
  grain: Grain;
}

/** A value below `price * OUTLIER_LOW` or above `price * OUTLIER_HIGH` is
 * flagged in the detail list (hint only — never removed, never moves the line). */
const OUTLIER_LOW = 0.4;
const OUTLIER_HIGH = 2.5;

/**
 * The TRUE extent of a set of observations (#2671) — min/max over each one's
 * declared range AND its own price.
 *
 * Three properties, each load-bearing and each with a silent failure mode:
 *
 *  - **`obsMin ?? value`**. Where an observation states a range (cell grain: one
 *    observation is a whole day of one counterparty, `value` its volume-weighted
 *    mean) the extent must reach it, or a one-counterparty day collapses to a
 *    single point over a day that really traded 4,20 to 8,80. Where it states
 *    none (line grain: an observation IS one line) the fallback reproduces the
 *    previous answer exactly.
 *  - **`value` folded in ALONGSIDE the range, not replaced by it** — the same
 *    call `cells.foldToPoint` makes one level down. The pair already contains
 *    `value` everywhere it is produced, so this changes no number on reachable
 *    data; it makes the extent TOTAL, so an incoherent pair upstream cannot
 *    yield a range excluding a price that is rendered inside it.
 *  - **The finiteness guard**, which is not belt-and-braces: `Math.max`/`Math.min`
 *    are NaN-POISONING, so a single non-finite extremum would collapse the whole
 *    result onto the seed — strictly worse than an unfolded loop, which skips
 *    NaN harmlessly (`NaN > h` is false). Unreachable today (`cells.num` rejects
 *    non-finite before an extremum is ever emitted), which is precisely why it
 *    is handled at the fold rather than left to be noticed later.
 *
 * SEEDED FROM NOTHING, on purpose. Seeding from `sources[0]` would reintroduce
 * the same poisoning one line higher and make the result depend on input ORDER.
 * With `±Infinity` seeds and a finiteness gate on `value` itself, the answer is
 * `max_i(hi_i)` / `min_i(lo_i)` over the finite rows regardless of order.
 *
 * An empty list — or one with no finite observation — yields `NaN` bounds rather
 * than a silently inverted `±Infinity` pair.
 */
export function observationExtent(sources: readonly AggSource[]): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const s of sources) {
    if (!Number.isFinite(s.value)) continue;
    const { obsMin, obsMax } = s;
    const hi = obsMax !== undefined && Number.isFinite(obsMax) ? Math.max(s.value, obsMax) : s.value;
    const lo = obsMin !== undefined && Number.isFinite(obsMin) ? Math.min(s.value, obsMin) : s.value;
    if (hi > max) max = hi;
    if (lo < min) min = lo;
  }
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : { min: NaN, max: NaN };
}

interface RawObs {
  date: string;
  value: number;
  channel: Channel | null;
  counterparty: string;
  provenance?: PointProvenance;
  /** Source lines behind this observation — 1 at line grain, the cell's ``n`` at cell grain. */
  lineCount: number;
  /** The observation's own price range (#2671), absent at line grain. */
  obsMin?: number;
  obsMax?: number;
  /** This observation's own price was weighted over only part of its rows (v34.6.0). */
  weightPartial?: boolean;
  /** The server's plausibility verdict (HOF-19), where it made one. */
  outOfBand?: OutOfBandMark;
  /** The series this observation came from — the drill-down's key (HOF-19). */
  seriesId?: string;
}

/**
 * The bucket's realised price — Σ money ÷ Σ traded quantity (HOF-6).
 *
 * Mirrors ``cells.foldToPoint`` on the server side of the same read, deliberately
 * and rule for rule, so the two levels cannot answer the same question
 * differently:
 *
 *   - a weighted observation contributes ``value × kg`` over ``Σ kg``;
 *   - **if ANY observation is weighted, the bucket is theirs.** An observation
 *     with no stated volume has no share of the period to claim, and letting it
 *     vote would drag the price by a quantity nobody can state. It stays a
 *     visible row in the panel and inside the observed range — it is excluded
 *     from the average, not hidden;
 *   - only when NOTHING is weighted does the fallback fire: the LINE-COUNT-
 *     weighted mean of the values, which is the best available reading of "what
 *     was this period's price" when no volume is known at all.
 *
 * `weighted` reports which branch ran, because the chart's footer names the
 * method and must not claim a weighting that did not happen.
 *
 * `partiallyWeighted` reports the SECOND half of that same honesty (v34.6.0
 * release gate): the weighted branch ran, but it did not run over everything.
 * `weighted` alone cannot express that — it is true whether one observation was
 * excluded or none was — and the footer reads it as "aller Geschäfte". The
 * excluded deal is still a visible panel row, still inside the observed range
 * and still counted, so a reader can see a transaction that provably had no
 * say in the headline price. Say so rather than round it up.
 */
function bucketPrice(obs: readonly RawObs[]): {
  price: number;
  weighted: boolean;
  partiallyWeighted: boolean;
  excludedCount: number;
  allExcluded: boolean;
} {
  // HOF-19 — the exclusion, and it happens BEFORE anything is summed.
  //
  // "Badge the point, drop it from the weighted-average line." The badge is the
  // rendering layer's; this is the drop, and it is the only place it may happen:
  // every average on this page — the chart's line, the day panel's Ø, the tile
  // sparkline and its year-over-year — is computed from a `BucketAgg.price`, so
  // one partition here covers all of them and no surface can be forgotten.
  //
  // What is NOT happening: the observation is not removed. It stays in
  // `sources`, in `uniqueSources`, inside `obsMin`/`obsMax`, and in `count`. It
  // is a visible row a reader can click through to its document. That
  // distinction is the entire ticket — a genuine premium sale that gets HIDDEN
  // makes the Preisspiegel lie in the direction that costs the farmer
  // negotiating room, while one that is merely EXCLUDED from the mean is
  // something they can see and disagree with.
  const trusted = obs.filter((o) => o.outOfBand === undefined);
  const excludedCount = obs.length - trusted.length;
  // **When every observation is marked, the fold runs over all of them anyway.**
  // The bucket must still produce a number: `price` is the chart's y value and
  // the panel's headline, and a bucket with no price would take the point off
  // the plot — which is exactly the hiding this ticket forbids, arrived at
  // through the back door. This is the realistic case, not an edge one: the
  // artefact that prompted HOF-19 lived in a channel series with exactly ONE
  // member. So the point is drawn, at its own value, and `allExcluded` is what
  // lets the chart mark it and the footnote say why.
  const folded = trusted.length > 0 ? trusted : obs;
  const allExcluded = excludedCount > 0 && trusted.length === 0;

  let wNum = 0; // Σ (value × kg)
  let wDen = 0; // Σ kg
  let uNum = 0; // Σ (value × lineCount)
  let uDen = 0; // Σ lineCount
  // An observation that ARRIVED partially weighted (``ObservedPoint.weightPartial``)
  // — UDB weighted the cell over only some of its rows. Unrecoverable from the
  // numbers here: ``cells.parseCell`` withholds the totals for such a cell, so
  // by this point it is indistinguishable from one that never had a weight.
  let anyIncomingPartial = false;
  for (const o of folded) {
    if (!Number.isFinite(o.value)) continue;
    if (o.weightPartial) anyIncomingPartial = true;
    const kg = catalogUnitKg(o.provenance, o.value);
    if (kg != null && kg > 0) {
      wNum += o.value * kg;
      wDen += kg;
    } else {
      uNum += o.value * o.lineCount;
      uDen += o.lineCount;
    }
  }
  // Two independent sources of partiality, and either is enough:
  //
  //  1. THIS fold dropped something — both denominators non-zero means the
  //     weighted branch is about to win AND there was something for it to win
  //     against. Derived here rather than at the call site because this is the
  //     only place that knows which observations were dropped; re-deriving it
  //     from the bucket would have to re-run the `catalogUnitKg` classification
  //     and could drift from it.
  //  2. An observation was ALREADY partial when it arrived. A bucket can be
  //     wholly made of such cells — every one weighted, none of them fully — in
  //     which case `uDen` is 0 and rule 1 sees nothing at all.
  if (wDen > 0) {
    return {
      price: wNum / wDen,
      weighted: true,
      partiallyWeighted: uDen > 0 || anyIncomingPartial,
      excludedCount,
      allExcluded,
    };
  }
  // No weighted observation won, so the price is a plain mean — but a mean OVER
  // partially-weighted cells still does not cover everything those cells did.
  if (uDen > 0) {
    return { price: uNum / uDen, weighted: false, partiallyWeighted: anyIncomingPartial, excludedCount, allExcluded };
  }
  return {
    price: folded[0].value,
    weighted: false,
    partiallyWeighted: Boolean(folded[0].weightPartial),
    excludedCount,
    allExcluded,
  };
}

function bucketAggFrom(date: string, endDate: string, obs: RawObs[]): BucketAgg {
  const { price, weighted, partiallyWeighted, excludedCount, allExcluded } = bucketPrice(obs);
  const sources: AggSource[] = obs
    .map((o) => ({
      counterparty: o.counterparty,
      channel: o.channel,
      value: o.value,
      date: o.date,
      provenance: o.provenance,
      lineCount: o.lineCount,
      // Spread only when stated: absent and `undefined` mean the same thing to
      // every consumer here, so this is cheap tidiness rather than a contract.
      ...(o.obsMin !== undefined ? { obsMin: o.obsMin } : {}),
      ...(o.obsMax !== undefined ? { obsMax: o.obsMax } : {}),
      isOutlier: price > 0 && (o.value < OUTLIER_LOW * price || o.value > OUTLIER_HIGH * price),
      ...(o.outOfBand !== undefined ? { outOfBand: o.outOfBand } : {}),
      ...(o.seriesId !== undefined ? { seriesId: o.seriesId } : {}),
    }))
    .sort((a, b) => b.value - a.value);
  const extent = observationExtent(sources);
  return {
    date,
    endDate,
    price,
    weighted,
    partiallyWeighted,
    obsMin: extent.min,
    obsMax: extent.max,
    // LINES, not observations: on the cell-backed read path one observation
    // stands for a whole day of one counterparty (#2646), so counting entries
    // here would quietly turn "N Beobachtungen" into "N counterparties".
    count: obs.reduce((total, o) => total + o.lineCount, 0),
    sources,
    uniqueSources: byTradedWeightDesc(collapseSources(sources)),
    counterpartyCount: new Set(sources.map((s) => s.counterparty)).size,
    excludedCount,
    allExcluded,
  };
}

/**
 * Collapse indistinguishable observations into one row per (counterparty, channel, price,
 * outlier flag) — #2539.
 *
 * The price is keyed at the materialiser's own precision — it rounds a fact's ``value`` to
 * 4 dp — and deliberately NOT at the 2-dp display string: two genuinely different prices
 * that both print "12,00" must stay two rows.
 *
 * ``channel`` is constant inside a per-channel column, so it only matters for the combined
 * bucket — where one buyer selling through two channels must never merge. ``isOutlier`` is
 * already a pure function of ``value`` within a bucket; keying on it makes "an outlier never
 * merges into a normal row" structural rather than incidental.
 *
 * **The DATE is in the key too, since HOF-6.** A bucket can span a whole month, so two
 * genuinely separate deliveries at the same price to the same buyer on two different days
 * would otherwise collapse into one row that names neither. Merging them was correct while
 * a bucket WAS a day (they were the same day by construction); it is a loss of fact now.
 *
 * Built by walking the already value-desc-sorted ``sources``, so the rows come out in that
 * same order with ties in first-seen order — no second sort, no new ordering to reason about.
 *
 * A group carries the FIRST member's ``provenance`` as a representative; every member stays
 * individually reachable via ``BucketAgg.sources``.
 *
 * Known, accepted edge — two sentinels stand in for an unidentifiable buyer, and two
 * different ones at the same price collapse into a single row: the materialiser's
 * "unbekannt" for an unattributable counterparty, and this app's own "—"
 * (``ownPriceAdapter``) when a fact carries neither name nor slug. Both are already
 * indistinguishable on screen.
 */
function collapseSources(sources: AggSource[]): AggSourceGroup[] {
  const groups = new Map<string, AggSourceGroup>();
  for (const s of sources) {
    // HOF-19 — the band verdict is in the key for the same reason `isOutlier`
    // is: a marked observation must never merge into an unmarked row. Here it
    // is stronger than tidiness, because the two kinds of row are labelled
    // DIFFERENTLY (one counted towards the Ø, one did not), and a merged row
    // could only carry one of those labels while standing for both. The series
    // is in the key too, because a collapsed row now OFFERS something scoped to
    // it (its own cell's source lines) and a merged row would drill into
    // whichever series happened to come first.
    const key =
      `${s.counterparty}␟${s.channel ?? ''}␟${s.date}␟${s.value.toFixed(4)}␟${s.isOutlier}` +
      `␟${s.outOfBand?.direction ?? ''}␟${s.seriesId ?? ''}`;
    const amount = s.provenance?.amount ?? null;
    const kg = catalogUnitKg(s.provenance, s.value);
    // #3593 — `bucketPrice`'s OWN predicate, spelled the same way here so the
    // row's label and the headline it sits under cannot drift apart. A stated 0
    // is excluded on exactly the same rule as an absent weight.
    const inFold = kg != null && kg > 0;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        ...s,
        lineCount: s.lineCount,
        amountSum: amount,
        kgSum: kg,
        weightShare: inFold ? 'all' : 'none',
      });
      continue;
    }
    existing.lineCount += s.lineCount;
    // A partial sum would misstate the total, so one amount-less member nulls the whole row.
    existing.amountSum = existing.amountSum == null || amount == null ? null : existing.amountSum + amount;
    // Same contract for the weight (#2649): a row's tonnage is the WHOLE row's or none.
    existing.kgSum = existing.kgSum == null || kg == null ? null : existing.kgSum + kg;
    // #3593 — deliberately NOT the whole-row-or-none contract above. A SUM is
    // only meaningful over every member, but "how much of this row counted" has
    // a truthful mixed answer, and `some` is exactly it. Once mixed, sticky.
    existing.weightShare =
      existing.weightShare === 'all' && inFold ? 'all' : existing.weightShare === 'none' && !inFold ? 'none' : 'some';
    // A row's RANGE is the UNION of its members' (#2671) — seeding it from the
    // first member alone would report one arbitrary member's spread for the
    // whole row. Deliberately NOT the "whole row's or none" contract the two
    // sums above use: a sum is only meaningful over every member, whereas an
    // extremum is an observed price that stands on its own.
    //
    // `finite(x, fallback)` for the same reason {@link observationExtent} guards
    // its fold: `Math.min`/`Math.max` are NaN-POISONING, so one non-finite
    // extremum would make the whole ROW's range NaN — and unlike the bucket's
    // extent, that NaN would be sticky, surviving every later merge.
    const finite = (x: number | undefined, fallback: number): number =>
      x !== undefined && Number.isFinite(x) ? x : fallback;
    if (s.obsMin !== undefined || existing.obsMin !== undefined) {
      existing.obsMin = Math.min(finite(existing.obsMin, existing.value), finite(s.obsMin, s.value));
    }
    if (s.obsMax !== undefined || existing.obsMax !== undefined) {
      existing.obsMax = Math.max(finite(existing.obsMax, existing.value), finite(s.obsMax, s.value));
    }
  }
  return [...groups.values()];
}

/**
 * Order the panel's rows by TRADED WEIGHT, descending (#2649).
 *
 * Sorting by price put the noise on top: a 40 kg sample sale outranked a 12 t
 * delivery whenever its €/kg was higher, so the row that moved the season's
 * money sat below rows that moved nothing. Weight-first makes the list read in
 * order of what it is worth reading — and since HOF-6 it is also the order the
 * LINE is computed in, so the top rows are the ones that actually set the price.
 *
 * Weightless rows (``kgSum == null``) go to the TAIL rather than being treated
 * as 0 kg: an unknown weight is not a small one. A KNOWN 0 kg still outranks
 * them.
 *
 * Input is ``collapseSources``' output, i.e. already in value-DESC / first-seen
 * order, and ``Array.prototype.sort`` is stable (ES2019), so equal weights — and
 * the whole weightless tail — keep exactly the price order they had.
 */
function byTradedWeightDesc(rows: AggSourceGroup[]): AggSourceGroup[] {
  return [...rows].sort((a, b) => {
    if (a.kgSum == null || b.kgSum == null) {
      if (a.kgSum == null && b.kgSum == null) return 0; // both unknown → keep price order
      return a.kgSum == null ? 1 : -1; // unknown weight to the tail
    }
    return b.kgSum - a.kgSum;
  });
}

/** Fold a flat observation list into ascending buckets at `grain`. */
function bucketize(obs: RawObs[], grain: Grain): BucketAgg[] {
  const byBucket = new Map<number, RawObs[]>();
  for (const o of obs) {
    const start = bucketStartEpochDay(toEpochDay(o.date), grain);
    const arr = byBucket.get(start);
    if (arr) arr.push(o);
    else byBucket.set(start, [o]);
  }
  return [...byBucket.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([start, list]) => bucketAggFrom(epochDayToIso(start), epochDayToIso(bucketEndEpochDay(start, grain)), list));
}

/**
 * Drop observations dated after `today`, BEFORE anything folds them.
 *
 * The chart clips its lines at "Heute", but since HOF-6 §3 that clip runs over
 * BUCKET STARTS, and a bucket start is not its contents: at 1M the August bucket
 * starts on the 1st, so with today the 21st it passes the clip while its price
 * has already averaged in a trade booked for the 28th. The reader is then shown
 * a future price as a realised one, left of the "Heute" line, with the weighting
 * silently wrong for the whole month. At 1W the same thing happens inside the
 * current week.
 *
 * Filtering at bucket level cannot fix it — by then the fold has happened — so
 * it has to be here, at the input. Future-dated observations are not exotic: a
 * Gutschrift can carry a value date ahead of its booking, and a Lieferschein can
 * be raised for a scheduled collection.
 *
 * Prior-year overlays are unaffected in the sense that matters: they are shifted
 * only AFTER this runs, so a genuine 2025 observation is never mistaken for a
 * future one.
 */
export function dropFutureObservations(series: readonly PriceSeries[], today: string): PriceSeries[] {
  const todayE = toEpochDay(today);
  const kept: PriceSeries[] = [];
  for (const s of series) {
    const points = s.points.filter((p) => toEpochDay(p.date) <= todayE);
    // A series that loses every point is dropped entirely rather than kept
    // empty: an empty series would still register its side and channel, and so
    // would put a line in the legend that the plot never draws.
    if (points.length === s.points.length) kept.push(s);
    else if (points.length > 0) kept.push({ ...s, points });
  }
  return kept;
}

/**
 * Collapse per-counterparty series into per-bucket prices, split by side and by
 * `(side, channel)`.
 *
 * `grain` decides how wide a bucket is; see {@link bucketPrice} for what a
 * bucket's number means.
 */
export function aggregateObservations(series: PriceSeries[], grain: Grain): AggregateModel {
  const perSide = new Map<PriceSide, RawObs[]>();
  const perSideChannel = new Map<GroupKey, RawObs[]>();
  const channelsSeen = new Map<PriceSide, Set<ChannelSlot>>();
  const bucketStarts = new Set<number>();

  for (const s of series) {
    const channel = s.channel ?? null;
    for (const p of s.points) {
      const obs: RawObs = {
        date: p.date,
        value: p.value,
        channel,
        counterparty: s.counterparty,
        provenance: p.provenance,
        // Absent on a line-grain point, which IS one line (#2646).
        lineCount: p.lineCount ?? 1,
        // Likewise absent at line grain — one line has no range (#2671).
        ...(p.obsMin !== undefined ? { obsMin: p.obsMin } : {}),
        ...(p.obsMax !== undefined ? { obsMax: p.obsMax } : {}),
        ...(p.weightPartial ? { weightPartial: true } : {}),
        ...(p.outOfBand !== undefined ? { outOfBand: p.outOfBand } : {}),
        seriesId: s.seriesId,
      };
      const sideList = perSide.get(s.side);
      if (sideList) sideList.push(obs);
      else perSide.set(s.side, [obs]);
      bucketStarts.add(bucketStartEpochDay(toEpochDay(p.date), grain));
      // EVERY observation gets a channel-axis home, including one that states no
      // channel — it lands in the {@link NO_CHANNEL} remainder. The `if (channel)`
      // this replaces filed the channel-less ones nowhere on this axis while
      // still counting them into `perSide` and `bucketStarts`, so they were
      // plotted and selectable but had no column to break down into, and the
      // drill-down panel rendered nothing on a click that had visibly landed.
      const slot: ChannelSlot = channel ?? NO_CHANNEL;
      const key = groupKey(s.side, slot);
      const list = perSideChannel.get(key);
      if (list) list.push(obs);
      else perSideChannel.set(key, [obs]);
      const seen = channelsSeen.get(s.side) ?? new Set<ChannelSlot>();
      seen.add(slot);
      channelsSeen.set(s.side, seen);
    }
  }

  const byGroup: Record<GroupKey, BucketAgg[]> = {};
  for (const [side, list] of perSide.entries()) byGroup[groupKey(side, 'all')] = bucketize(list, grain);
  for (const [key, list] of perSideChannel.entries()) byGroup[key] = bucketize(list, grain);

  // Canonical channel order, then the remainder — it is the residue of the axis,
  // so it reads last in a legend and sits last among the panel's columns.
  //
  // A side whose observations ALL decline a channel reports no slots at all
  // rather than the lone remainder: the channel axis is not live for it, and
  // saying `['ohne']` would turn `hasChannels` on for an axis with one member
  // and rename that side's only column from the side to "Ohne Kanal".
  const channelsBySide: Partial<Record<PriceSide, ChannelSlot[]>> = {};
  for (const [side, seen] of channelsSeen.entries()) {
    const channels: ChannelSlot[] = CHANNELS.filter((c) => seen.has(c));
    if (channels.length === 0) continue;
    channelsBySide[side] = seen.has(NO_CHANNEL) ? [...channels, NO_CHANNEL] : channels;
  }

  return {
    byGroup,
    sidesPresent: PRICE_SIDES.filter((s) => perSide.has(s)),
    channelsBySide,
    bucketDates: [...bucketStarts].sort((a, b) => a - b).map(epochDayToIso),
    grain,
  };
}
